import { useEffect, useState, useRef } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const useReveal = (threshold = 0.12) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setShown(true)),
      { threshold }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, shown };
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const { ref, shown } = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-[1200ms] ease-out ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Divider = () => (
  <div className="container-luxury">
    <div className="max-w-md mx-auto py-12 md:py-16 flex items-center justify-center gap-6">
      <span className="h-px w-16 bg-stone-400/50" />
      <span className="w-1.5 h-1.5 rounded-full bg-stone-500/60" />
      <span className="h-px w-16 bg-stone-400/50" />
    </div>
  </div>
);

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F4EFE6] relative overflow-x-hidden">
      <Navigation />

      {/* ───────── HERO ───────── */}
      <section
        className="relative pt-32 md:pt-44 pb-20 md:pb-28 overflow-hidden"
        data-testid="about-hero"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 50% 30%, rgba(255,251,240,1) 0%, rgba(244,239,230,0) 65%)",
          }}
        />
        <div className="container-luxury relative z-10">
          <Reveal>
            <div className="text-center max-w-4xl mx-auto">
              <p className="text-[11px] tracking-[0.5em] uppercase text-stone-500 mb-8">
                UNIXORN — The Manifesto
              </p>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold text-stone-900 tracking-tight leading-[0.95] mb-10">
                ABOUT<br />UNIXORN<sup className="text-2xl md:text-3xl lg:text-4xl align-top">™</sup>
              </h1>
              <p className="text-lg md:text-2xl text-stone-600 italic max-w-xl mx-auto">
                Unique &amp; Original.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── OPENING — THE WORLD DOES NOT NEED MORE COPIES ───────── */}
      <section className="relative py-24 md:py-36 bg-[#EBE3D2] border-y border-stone-300/50">
        <div className="container-luxury">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-[11px] tracking-[0.5em] uppercase text-stone-500 mb-10">
                The Belief
              </p>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-display font-bold text-stone-900 tracking-tight leading-[1.1] mb-10">
                The world does not<br />need more copies.<br />
                <span className="italic text-stone-700 font-normal">It needs more originals.</span>
              </h2>
              <div className="space-y-5 text-stone-700 text-base md:text-lg leading-relaxed mt-12">
                <p>
                  UNIXORN was founded on a simple belief — and the name itself
                  carries it. Two ideas, fused into one mark:
                </p>
                <p className="font-semibold text-stone-900 tracking-wide">
                  <span className="tracking-[0.2em]">UNIQUE</span>
                  <span className="mx-3 text-stone-400">·</span>
                  <span className="tracking-[0.2em]">ORIGINAL</span>
                </p>
                <p>
                  A reminder that true style is not about following trends. It
                  is about expressing individuality with confidence.
                </p>
                <p className="text-stone-500 italic pt-3">
                  In a culture filled with imitation, UNIXORN was created for
                  those who choose their own path.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Divider />

      {/* ───────── MORE THAN CLOTHING ───────── */}
      <section className="relative py-24 md:py-36 bg-[#F4EFE6]">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-24 items-center max-w-6xl mx-auto">
            <Reveal>
              <p className="text-[11px] tracking-[0.45em] uppercase text-stone-500 mb-6">
                The Brand
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-stone-900 tracking-tight mb-10 leading-[1.05]">
                MORE THAN<br />CLOTHING.
              </h2>
              <div className="space-y-5 text-stone-700 text-base md:text-lg leading-relaxed">
                <p>
                  UNIXORN is a premium lifestyle and streetwear brand built
                  around self-expression, creativity, and personal evolution.
                </p>
                <p>
                  Every collection is designed to inspire people to embrace
                  what makes them different — and turn that difference into
                  their greatest strength.
                </p>
                <p className="text-stone-900 font-semibold italic pt-3">
                  Individuality is a superpower.
                </p>
                <p>
                  Whether you&rsquo;re creating, building, leading, performing,
                  or dreaming — UNIXORN exists to remind you that your
                  uniqueness is your advantage.
                </p>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div className="relative aspect-[4/5] overflow-hidden bg-[#F5F0E5] shadow-[0_40px_100px_-40px_rgba(120,100,72,0.32)]">
                <img
                  src="/assets/images/cream/cream-set.png"
                  alt="UNIXORN — More than clothing"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Divider />

      {/* ───────── OUR PHILOSOPHY ───────── */}
      <section className="relative py-24 md:py-36 bg-[#EBE3D2] border-y border-stone-300/50">
        <div className="container-luxury">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-[11px] tracking-[0.5em] uppercase text-stone-500 mb-10">
                Our Philosophy
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-stone-900 tracking-tight leading-[1.05] mb-12">
                LUXURY SHOULD NOT<br />
                <span className="italic text-stone-700 font-normal">be reserved for the few.</span>
              </h2>
              <div className="space-y-6 text-stone-700 text-base md:text-lg leading-relaxed">
                <p>
                  It should be a reflection of authenticity, confidence, and
                  purpose.
                </p>
                <p>
                  That&rsquo;s why we focus on timeless design, premium
                  materials, and elevated essentials — pieces that can be worn
                  anywhere while making a statement without saying a word.
                </p>
                <p className="text-stone-500 italic pt-3">
                  We don&rsquo;t chase trends.<br />
                  We create pieces designed to outlast them.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── THE SYMBOL ───────── */}
      <section className="relative py-24 md:py-36 bg-[#F4EFE6]">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-24 items-center max-w-6xl mx-auto">
            <Reveal>
              <div className="relative aspect-square overflow-hidden bg-[#F5F0E5] shadow-[0_40px_100px_-40px_rgba(120,100,72,0.32)]">
                <img
                  src="/assets/images/cream/signature-statement-tee.png"
                  alt="The UNIXORN symbol"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-5 left-5 px-3.5 py-2 bg-[#F4EFE6]/85 backdrop-blur-sm border border-stone-400/40">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-stone-700 font-semibold">
                    The Symbol
                  </span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <p className="text-[11px] tracking-[0.45em] uppercase text-stone-500 mb-6">
                The Symbol
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-stone-900 tracking-tight mb-10 leading-[1.05]">
                IMAGINATION.<br />POSSIBILITY.<br />
                <span className="italic text-stone-700 font-normal">The courage to be different.</span>
              </h2>
              <div className="space-y-5 text-stone-700 text-base md:text-lg leading-relaxed">
                <p>
                  The UNIXORN symbolizes imagination, possibility, and the
                  courage to be different.
                </p>
                <p>
                  Like the mythical creature that inspires our name, we believe
                  every individual possesses something rare and extraordinary.
                </p>
                <p className="text-stone-900 font-semibold pt-3">
                  Our mission is simple — help people embrace it.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Divider />

      {/* ───────── OUR PROMISE ───────── */}
      <section className="relative py-24 md:py-36 bg-[#EBE3D2] border-y border-stone-300/50">
        <div className="container-luxury">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-[11px] tracking-[0.5em] uppercase text-stone-500 mb-10">
                Our Promise
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-stone-900 tracking-tight leading-[1.05] mb-14">
                EVERY PIECE,<br />WITH INTENTION.
              </h2>

              <ul className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 mb-14">
                {[
                  { label: "Premium", value: "Quality" },
                  { label: "Timeless", value: "Aesthetics" },
                  { label: "Distinct", value: "Identity" },
                ].map((p) => (
                  <li key={p.label} className="text-center">
                    <p className="text-[10px] tracking-[0.4em] uppercase text-stone-500 font-semibold mb-2">
                      {p.label}
                    </p>
                    <p className="text-2xl md:text-3xl font-display font-bold text-stone-900 tracking-tight">
                      {p.value}
                    </p>
                  </li>
                ))}
              </ul>

              <div className="space-y-3 text-stone-700 text-base md:text-lg leading-relaxed italic">
                <p>Built for those who refuse to blend in.</p>
                <p>Built for those who create their own lane.</p>
                <p className="text-stone-900 font-semibold not-italic tracking-wider pt-4">
                  Built for the Unique.<br />Built for the Original.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── UNLEASH YOUR MAGIC ───────── */}
      <section className="relative py-32 md:py-44 bg-[#F4EFE6]">
        <div className="container-luxury">
          <Reveal>
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-[11px] tracking-[0.5em] uppercase text-stone-500 mb-10">
                The Mindset
              </p>
              <h2 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold text-stone-900 tracking-tight leading-[0.95] mb-12">
                UNLEASH<br />YOUR MAGIC<sup className="text-2xl md:text-3xl lg:text-4xl align-top">™</sup>
              </h2>
              <div className="space-y-6 text-stone-700 text-lg md:text-xl leading-relaxed">
                <p>
                  UNIXORN<sup className="text-xs align-top">™</sup> is more than a brand.<br />
                  It is a mindset.
                </p>
                <p>
                  A reminder to think bigger.<br />
                  Dream boldly.<br />
                  Live authentically.
                </p>
                <p className="text-stone-500 italic pt-6">
                  Your uniqueness is your power.<br />
                  Your originality is your legacy.
                </p>
                <p className="text-stone-900 font-semibold tracking-[0.15em] pt-10 text-base md:text-lg">
                  UNLEASH YOUR MAGIC<sup className="text-[10px] align-top">™</sup>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── WEAR THE MINDSET ───────── */}
      <section className="relative py-24 md:py-32 bg-[#EBE3D2] border-t border-stone-300/50">
        <div className="container-luxury">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-stone-900 tracking-tight leading-[1.02] mb-10">
                WEAR THE MINDSET
              </h2>
              <a
                href="https://shop.unixornworldwide.com"
                className="inline-block px-12 py-6 bg-stone-900 text-[#F4EFE6] hover:bg-stone-800 transition-colors duration-500 uppercase tracking-[0.35em] font-bold text-sm"
                data-testid="wear-the-mindset-cta"
              >
                Explore the Collection
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
